import { type Maybe, type SearchResult } from '@atlassian/search-client/common/types';
import { type ThirdPartyRestrictionLevel } from '@atlassian/search-client/search-page/types';

import { is3pProductKey } from '../constants/product-keys';
import {
	type ConnectorSource,
	isConnectorSource,
	type ThirdPartyConfigsBootstrap,
} from '../constants/schemas/3p-config';
import { ConnectorSources } from '../constants/schemas/connector-sources';
import { VIEWED_EVENT } from '../ui/event-type-message';

import { isProductSmartlink } from './connectors';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getProductFromThirdPartyId } from './get-product-from-third-party-id';

export interface SearchResultGraphDocument extends SearchResult {
	excerpt: string;
	permissionLevel: Maybe<ThirdPartyRestrictionLevel>;
	subtype: string;
	integrationId: string;
	lastModifiedDate: string;
	initialContributors: {
		name: string;
	}[];
}

const GOOGLE_INTEGRATION_ARI = 'ari:cloud:platform::integration/google';
export const isGoogleDocument: (partial: SearchResult) => boolean = (
	partial: SearchResult,
): boolean =>
	partial.__typename === 'SearchResultGoogleDocument' ||
	(isGraphDocument(partial) &&
		partial.integrationId === GOOGLE_INTEGRATION_ARI &&
		partial.subtype === 'document');

export const isGraphDocument: (partial: SearchResult) => partial is SearchResultGraphDocument = (
	partial: SearchResult,
): partial is SearchResultGraphDocument => partial.__typename === 'SearchResultGraphDocument';

const SMART_LINK_ENTITY_REGEX = /^ari:.*::remote-link/;

export const isAriSmartLink: (resultAri: string) => boolean = (resultAri: string): boolean =>
	SMART_LINK_ENTITY_REGEX.test(resultAri);

export const isResultSmartLink: ({
	connectorType,
	resultAri,
	eventType,
	productKey,
	thirdPartyConfigs,
}: {
	resultAri: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	productKey?: string;
	eventType?: string;
	connectorType?: string | null;
}) => boolean = ({
	connectorType,
	resultAri,
	eventType,
	productKey,
	thirdPartyConfigs,
}: {
	resultAri: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	productKey?: string;
	eventType?: string;
	connectorType?: string | null;
}): boolean =>
	connectorType === ConnectorSources.Smartlinks ||
	isAriSmartLink(resultAri) ||
	isProductSmartlink({ productKey, thirdPartyConfigs }) ||
	eventType === VIEWED_EVENT.SL_VIEWED ||
	eventType === VIEWED_EVENT.SL_CLICKED;

const getProduct3PConfig = (
	productKey: string | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	return productKey && is3pProductKey(productKey) ? thirdPartyConfigs[productKey] : null;
};

/**
 * Returns the connectorType based on the parameters provided.
 * @param connectorType - This takes highest precedence and is used if it is valid and provided.
 * @param resultNodeId - This is used to determine if the result is a smartlink.
 * @param eventType - This is used to determine if the result is a smartlink.
 * @param productKey - This is used to find the third party config associated with productKey.
 * @param thirdPartyConfigs - This is used to find the third party config associated with productKey.
 * @returns
 */
export const getResultConnectorSource: ({
	connectorType,
	resultNodeId,
	eventType,
	productKey,
	thirdPartyConfigs,
}: {
	connectorType?: string | null;
	resultNodeId: string;
	eventType?: string;
	productKey?: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
}) => ConnectorSource | undefined = ({
	connectorType,
	resultNodeId,
	eventType,
	productKey,
	thirdPartyConfigs,
}: {
	connectorType?: string | null;
	resultNodeId: string;
	eventType?: string;
	productKey?: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
}): ConnectorSource | undefined => {
	// Return the connectorType if it's already valid
	if (isConnectorSource(connectorType)) {
		return connectorType;
	}

	// connectorType will not exist for the following cases:
	// * when search GraphQL response returns null for connectorType
	// * when the given API doesn't return a connectorType. E.g. user documents.
	// as a result, we use fallback logic to determine the connectorType.

	if (
		isResultSmartLink({
			connectorType,
			resultAri: resultNodeId,
			eventType,
			productKey,
			thirdPartyConfigs,
		})
	) {
		return ConnectorSources.Smartlinks;
	}

	const config = getProduct3PConfig(productKey, thirdPartyConfigs);

	// No config found for the product
	if (!config) {
		return undefined;
	}

	if (config.isFederated) {
		return ConnectorSources.Federated;
	}

	// Return the single connector source if it's valid
	if (config.connectorSources?.length === 1) {
		const source = config.connectorSources[0];
		if (isConnectorSource(source)) {
			return source;
		}
	}

	// Default to Full for any other case where config exists
	return ConnectorSources.Full;
};
